import React, { useEffect, useState } from "react";
import api, { employeesApi, salaryPaymentsApi } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { PaymentSourceSelector } from "../components/PaymentSourceSelector";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { exportToCsv, timestampedName } from "../lib/exportToCsv";
import ExportCurrencyButton, { buildExportPreamble } from "../components/ExportCurrencyButton";
import {
  DollarSign,
  Calendar,
  CheckCircle2,
  Clock,
  TrendingUp,
  Wallet,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Building2,
  CreditCard,
  Bitcoin,
  SplitSquareHorizontal,
  Gift,
  Trash2,
  Eye,
  Copy,
  Banknote,
  ChevronUp,
  ChevronDown,
  X,
  Filter,
} from "lucide-react";

// Helper to extract currency from PM bonus notes (fallback for old records without bonus_currency)
const getCurrencyFromNotes = (notes) => {
  if (!notes) return null;
  // Format: "PM bonus (3.0%) - Project | Revenue: UAH52020, Costs: UAH12000, Profit: UAH40020"
  const match = notes.match(/Revenue:\s*(UAH|USD|EUR)/);
  return match ? match[1] : null;
};

import { useAuth } from "../context/AuthContext";

const TeamExpensesPage = () => {
  const { user: currentUser } = useAuth();
  const { formatAmount, convertAmount, convertAmountExplicit, displayCurrency, getCurrencySymbol, rates, rateDate } = useCurrency();
  const [employees, setEmployees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [partialPaymentDialog, setPartialPaymentDialog] = useState({ open: false, payment: null });
  const [partialAmount, setPartialAmount] = useState("");
  const [partialCurrency, setPartialCurrency] = useState("UAH");

  const [bonusDialog, setBonusDialog] = useState({ open: false, employee: null });
  const [bonusAmount, setBonusAmount] = useState("");
  const [bonusType, setBonusType] = useState("project_bonus");
  const [bonusCurrency, setBonusCurrency] = useState("UAH");

  // Payment details modal
  const [paymentDetailsModal, setPaymentDetailsModal] = useState({ open: false, employee: null });

  // Filters & sorting
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMethod, setFilterMethod] = useState("all");
  const [searchName, setSearchName] = useState("");
  const [showBonuses, setShowBonuses] = useState(true);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  // Paid details dialog
  const [paidDetailsDialog, setPaidDetailsDialog] = useState({ open: false, paymentId: null, isStaffBonus: false });
  const [paidBy, setPaidBy] = useState("");
  const [paidSource, setPaidSource] = useState("");
  const [adminStaff, setAdminStaff] = useState([]);

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const fetchData = async () => {
    try {
      const [employeesRes, paymentsRes, summaryRes] = await Promise.all([
        employeesApi.getAll(true),
        salaryPaymentsApi.getAll({ month: selectedMonth }),
        salaryPaymentsApi.getMonthlySummary(),
      ]);
      setEmployees(employeesRes.data);
      setPayments(paymentsRes.data);
      setMonthlySummary(summaryRes.data);
      // Load employees for "Paid By" dropdown
      try {
        const staffRes = await api.get("/employees?is_active=true");
        setAdminStaff(staffRes.data || []);
      } catch { /* ignore */ }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePayments = async () => {
    try {
      await salaryPaymentsApi.generateMonth(selectedMonth);
      toast.success(`Payments generated for ${formatMonth(selectedMonth)}`);
      setGenerateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate payments");
    }
  };

  const handleMarkPaid = async (paymentId, isStaffBonus = false) => {
    // Open paid details dialog — pre-fill with current user
    setPaidDetailsDialog({ open: true, paymentId, isStaffBonus });
    setPaidBy(currentUser?.id || "");
    setPaidSource("");
  };

  const handleConfirmPaid = async () => {
    const { paymentId, isStaffBonus } = paidDetailsDialog;
    try {
      if (isStaffBonus) {
        await api.put(`/staff-bonuses/${paymentId}?status=paid`);
      } else {
        const updateData = { status: "paid" };
        if (paidBy) updateData.paid_by = paidBy;
        if (paidSource.trim()) updateData.paid_source = paidSource.trim();
        await salaryPaymentsApi.update(paymentId, updateData);
      }
      toast.success("Payment marked as paid");
      setPaidDetailsDialog({ open: false, paymentId: null, isStaffBonus: false });
      fetchData();
    } catch (error) {
      toast.error("Failed to update payment");
    }
  };

  const handleMarkAllPaid = async () => {
    try {
      const pendingPayments = payments.filter(p => p.status === "pending" || p.status === "partial");
      await Promise.all(pendingPayments.map(p => 
        salaryPaymentsApi.update(p.id, { status: "paid" })
      ));
      toast.success(`${pendingPayments.length} payments marked as paid`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update payments");
    }
  };

  const handlePartialPayment = async () => {
    const amount = parseFloat(partialAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    try {
      // Create a new partial payment record
      await salaryPaymentsApi.create({
        employee_id: partialPaymentDialog.payment.employee_id,
        amount: amount,
        currency: partialCurrency,
        month: partialPaymentDialog.payment.month,
        notes: `Partial payment`,
        is_partial: true,
      });
      // Mark the partial payment as paid immediately
      const newPayments = await salaryPaymentsApi.getAll({ month: selectedMonth });
      const newPartial = newPayments.data.find(p => 
        p.employee_id === partialPaymentDialog.payment.employee_id && 
        p.is_partial && 
        p.status === "pending"
      );
      if (newPartial) {
        await salaryPaymentsApi.update(newPartial.id, { status: "paid" });
      }
      toast.success(`Partial payment $${amount.toLocaleString()} added`);
      setPartialPaymentDialog({ open: false, payment: null });
      setPartialAmount("");
      setPartialCurrency("UAH");
      fetchData();
    } catch (error) {
      toast.error("Failed to create partial payment");
    }
  };

  const handleAddBonus = async () => {
    const amount = parseFloat(bonusAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    try {
      await salaryPaymentsApi.create({
        employee_id: bonusDialog.employee.employee_id,
        amount: amount,
        month: selectedMonth,
        notes: `Bonus: ${bonusType} (${bonusCurrency})`,
        bonus_type: bonusType,
      });
      const currencySymbol = bonusCurrency === "USD" ? "$" : "₴";
      toast.success(`Bonus ${currencySymbol}${amount.toLocaleString()} added`);
      setBonusDialog({ open: false, employee: null });
      setBonusAmount("");
      setBonusType("project_bonus");
      setBonusCurrency("UAH");
      fetchData();
    } catch (error) {
      toast.error("Failed to add bonus");
    }
  };

  const handleDeletePayment = async (paymentId, isStaffBonus = false) => {
    try {
      if (isStaffBonus) {
        // Delete from staff_bonuses collection
        await api.delete(`/staff-bonuses/${paymentId}`);
      } else {
        // Delete from salary_payments collection
        await salaryPaymentsApi.delete(paymentId);
      }
      toast.success("Payment deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete payment");
    }
  };

  const handleDeleteAllMonthPayments = async () => {
    try {
      const monthPayments = payments.filter(p => p.month === selectedMonth);
      await Promise.all(monthPayments.map(p => salaryPaymentsApi.delete(p.id)));
      toast.success(`Deleted ${monthPayments.length} payments for ${formatMonth(selectedMonth)}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete payments");
    }
  };

  const getPaymentMethodDisplay = (method, bank, cryptoNetwork) => {
    if (method === "fop") {
      return (
        <div className="flex items-center gap-1.5">
          <Building2 className="h-4 w-4 text-sky-400" />
          <span className="text-xs text-sky-400 font-medium">FOP</span>
        </div>
      );
    }
    if (method === "card") {
      if (bank === "monobank") {
        return (
          <div className="flex items-center gap-1.5">
            <CreditCard className="h-4 w-4 text-zinc-300" />
            <span className="text-xs text-zinc-300 font-medium">Mono</span>
          </div>
        );
      }
      if (bank === "privatbank") {
        return (
          <div className="flex items-center gap-1.5">
            <CreditCard className="h-4 w-4 text-green-400" />
            <span className="text-xs text-green-400 font-medium">Privat</span>
          </div>
        );
      }
      return (
        <div className="flex items-center gap-1.5">
          <CreditCard className="h-4 w-4 text-zinc-400" />
          <span className="text-xs text-zinc-400 font-medium">Card</span>
        </div>
      );
    }
    if (method === "crypto") {
      return (
        <div className="flex items-center gap-1.5">
          <Bitcoin className="h-4 w-4 text-orange-400" />
          <span className="text-xs text-orange-400 font-medium">{cryptoNetwork || "USDT"}</span>
        </div>
      );
    }
    return <span className="text-xs text-zinc-600">—</span>;
  };

  // Get payment details for tooltip
  const getPaymentDetails = (employee) => {
    const pm = employee.payment_methods;
    if (!pm) return null;
    
    const details = [];
    if (pm.card) {
      if (pm.card.card_number) details.push(`Card: ${pm.card.card_number}`);
      if (pm.card.recipient_iban) details.push(`IBAN: ${pm.card.recipient_iban}`);
      if (pm.card.bank) details.push(`Bank: ${pm.card.bank}`);
      if (pm.card.recipient_name) details.push(`Name: ${pm.card.recipient_name}`);
    }
    if (pm.fop) {
      if (pm.fop.iban) details.push(`FOP IBAN: ${pm.fop.iban}`);
      if (pm.fop.name) details.push(`FOP: ${pm.fop.name}`);
      if (pm.fop.ipn) details.push(`IPN: ${pm.fop.ipn}`);
    }
    if (pm.crypto) {
      if (pm.crypto.wallet_address) details.push(`Crypto: ${pm.crypto.wallet_address}`);
      if (pm.crypto.network) details.push(`Network: ${pm.crypto.network}`);
    }
    return details.length > 0 ? details.join("\n") : null;
  };

  const formatMonth = (monthStr) => {
    // Parse month string "YYYY-MM" correctly to avoid timezone issues
    const [year, month] = monthStr.split("-").map(Number);
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return `${monthNames[month - 1]} ${year}`;
  };

  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split("-").map(Number);
    let newMonth = month + direction;
    let newYear = year;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, "0")}`);
  };

  // Helper to convert amount to UAH for consistent summing
  const toUAH = (amount, currency) => {
    if (!amount) return 0;
    if (currency === "UAH" || !currency) return amount;
    if (currency === "USD") return amount * (rates?.USD || 41.5);
    if (currency === "EUR") return amount * (rates?.EUR || 43.5);
    return amount;
  };

  // Calculate stats for selected month (all converted to UAH for consistent totals)
  // First group by employee to calculate pending correctly
  const employeeStatsMap = {};
  payments.forEach(p => {
    if (!employeeStatsMap[p.employee_id]) {
      employeeStatsMap[p.employee_id] = {
        salaryAmount: 0,    // Main salary obligation
        bonusAmount: 0,     // Total bonus obligation (pending or paid)
        mainPaid: 0,        // Paid amount on the main salary record(s)
        partialPaid: 0,     // Paid interim partials toward the salary
        bonusPaid: 0,       // Paid bonuses
      };
    }
    const amountUAH = toUAH(p.amount, p.currency);
    const e = employeeStatsMap[p.employee_id];

    // Main salary (not partial, not bonus)
    if (!p.is_partial && !p.is_bonus && !p.bonus_type) {
      e.salaryAmount += amountUAH;
      if (p.status === "paid") e.mainPaid += amountUAH;
    } else if (p.is_bonus || p.bonus_type) {
      e.bonusAmount += amountUAH;
      if (p.status === "paid") e.bonusPaid += amountUAH;
    } else {
      if (p.status === "paid") e.partialPaid += amountUAH;
    }
  });
  // Once the main salary is marked fully paid it captures the entire salary,
  // so interim partials are already included — don't add them again (would
  // double-count the advance). Use partials only while the main is unpaid.
  Object.values(employeeStatsMap).forEach(e => {
    e.totalPaid = (e.mainPaid > 0 ? e.mainPaid : e.partialPaid) + e.bonusPaid;
  });
  
  // Now calculate totals
  const monthStats = Object.values(employeeStatsMap).reduce(
    (acc, emp) => {
      const totalObligation = emp.salaryAmount + emp.bonusAmount;
      acc.paid += emp.totalPaid;
      // Pending is what remains: total obligation - paid (but not less than 0)
      const remaining = Math.max(0, totalObligation - emp.totalPaid);
      acc.pending += remaining;
      acc.total += totalObligation;  // Total = salary + bonuses
      return acc;
    },
    { paid: 0, pending: 0, partial: 0, total: 0 }
  );

  // Group payments by employee for display
  const employeePayments = {};
  payments.forEach(p => {
    if (!employeePayments[p.employee_id]) {
      employeePayments[p.employee_id] = {
        employee_id: p.employee_id,
        employee_id: p.employee_id,
        employee_name: p.employee_name,
        main_payment: null,
        partial_payments: [],
        bonus_payments: [],
        total_paid_uah: 0, // Store in UAH for consistent summing
        payment_method: p.payment_method,
        payment_method_bank: p.payment_method_bank,
        crypto_network: p.crypto_network,
      };
    }
    
    // Check if it's a bonus payment
    if (p.is_bonus || p.bonus_type) {
      employeePayments[p.employee_id].bonus_payments.push(p);
    } else if (p.is_partial) {
      employeePayments[p.employee_id].partial_payments.push(p);
    } else {
      // Main salary payment
      employeePayments[p.employee_id].main_payment = p;
    }
  });
  // Compute paid total after grouping: a paid main salary record already
  // represents the whole salary, so interim partials must not be added on top
  // (otherwise the advance is counted twice). Fall back to paid partials while
  // the main salary is still unpaid.
  Object.values(employeePayments).forEach(emp => {
    const mainPaid = emp.main_payment?.status === "paid"
      ? toUAH(emp.main_payment.amount, emp.main_payment.currency) : 0;
    const partialsPaid = emp.partial_payments
      .filter(p => p.status === "paid")
      .reduce((s, p) => s + toUAH(p.amount, p.currency), 0);
    const bonusesPaid = emp.bonus_payments
      .filter(b => b.status === "paid")
      .reduce((s, b) => s + toUAH(b.amount, b.currency), 0);
    emp.total_paid_uah = (mainPaid > 0 ? mainPaid : partialsPaid) + bonusesPaid;
  });
  const groupedPayments = Object.values(employeePayments);

  // Filtering
  const filteredGrouped = groupedPayments.filter(item => {
    if (searchName && !item.employee_name?.toLowerCase().includes(searchName.toLowerCase())) return false;
    if (filterMethod !== "all" && item.payment_method !== filterMethod) return false;
    if (filterStatus !== "all") {
      const mainP = item.main_payment;
      const isFullyPaid = mainP?.status === "paid" && item.bonus_payments.every(b => b.status === "paid");
      const hasPaid = item.total_paid_uah > 0;
      if (filterStatus === "paid" && !isFullyPaid) return false;
      if (filterStatus === "pending" && (isFullyPaid || hasPaid)) return false;
      if (filterStatus === "partial" && (!hasPaid || isFullyPaid)) return false;
    }
    return true;
  });

  // Sorting
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedGrouped = [...filteredGrouped].sort((a, b) => {
    if (!sortKey) return 0;
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortKey === "name") return (a.employee_name || "").localeCompare(b.employee_name || "") * dir;
    if (sortKey === "method") return (a.payment_method || "").localeCompare(b.payment_method || "") * dir;
    if (sortKey === "salary") return ((a.main_payment?.amount || 0) - (b.main_payment?.amount || 0)) * dir;
    if (sortKey === "total") {
      const aTotal = (a.main_payment?.amount || 0) + a.bonus_payments.reduce((s, bp) => s + bp.amount, 0);
      const bTotal = (b.main_payment?.amount || 0) + b.bonus_payments.reduce((s, bp) => s + bp.amount, 0);
      return (aTotal - bTotal) * dir;
    }
    return 0;
  });

  // Method summary - calculated from FILTERED data
  const methodSummary = filteredGrouped.reduce((acc, item) => {
    const method = item.payment_method || "other";
    const key = method === "card" ? (item.payment_method_bank || "card") : method;
    if (!acc[key]) acc[key] = { pending: 0, paid: 0, total: 0, count: 0 };
    const mainAmt = toUAH(item.main_payment?.amount || 0, item.main_payment?.currency);
    const bonusAmt = item.bonus_payments.reduce((s, b) => s + toUAH(b.amount, b.currency), 0);
    const total = mainAmt + bonusAmt;
    acc[key].total += total;
    acc[key].paid += item.total_paid_uah;
    acc[key].pending += Math.max(0, total - item.total_paid_uah);
    acc[key].count++;
    return acc;
  }, {});

  const handleExport = (targetCurrency = "ORIGINAL") => {
    const convert = targetCurrency !== "ORIGINAL";
    const mapRow = (base) => {
      const native = Number(base.amount) || 0;
      const nativeCur = (base.currency || "UAH").toUpperCase();
      const exportedAmount = convert
        ? Math.round(convertAmountExplicit(native, nativeCur, targetCurrency) * 100) / 100
        : native;
      const nativePaid = Number(base.paid_amount) || 0;
      const exportedPaid = convert && nativePaid
        ? Math.round(convertAmountExplicit(nativePaid, nativeCur, targetCurrency) * 100) / 100
        : (base.paid_amount ?? "");
      return {
        ...base,
        amount: exportedAmount,
        currency: convert ? targetCurrency : nativeCur,
        paid_amount: exportedPaid,
        ...(convert ? { original_amount: native, original_currency: nativeCur } : {}),
      };
    };
    const rows = [];
    sortedGrouped.forEach((item) => {
      const main = item.main_payment;
      if (main) {
        rows.push(mapRow({
          employee: item.employee_name || "",
          method: item.payment_method || "",
          kind: "Salary",
          amount: main.amount ?? "",
          currency: main.currency || "",
          status: main.status || "",
          paid_date: main.paid_date || "",
          paid_amount: main.paid_amount ?? "",
          notes: main.notes || "",
          month: selectedMonth,
        }));
      }
      (item.bonus_payments || []).forEach((bp) => {
        rows.push(mapRow({
          employee: item.employee_name || "",
          method: item.payment_method || "",
          kind: bp.payment_type === "pm_bonus" ? "PM bonus" : "Bonus",
          amount: bp.amount ?? "",
          currency: bp.currency || "",
          status: bp.status || "",
          paid_date: bp.paid_date || "",
          paid_amount: bp.paid_amount ?? "",
          notes: bp.notes || "",
          month: selectedMonth,
        }));
      });
    });
    if (rows.length === 0) {
      toast.error("Nothing to export with current filters");
      return;
    }
    const columns = [
      { key: "employee", header: "Employee" },
      { key: "method", header: "Method" },
      { key: "kind", header: "Kind" },
      { key: "amount", header: convert ? `Amount (${targetCurrency})` : "Amount" },
      { key: "currency", header: "Currency" },
      ...(convert ? [
        { key: "original_amount", header: "Original Amount" },
        { key: "original_currency", header: "Original Currency" },
      ] : []),
      { key: "status", header: "Status" },
      { key: "paid_date", header: "Paid Date" },
      { key: "paid_amount", header: convert ? `Paid Amount (${targetCurrency})` : "Paid Amount" },
      { key: "notes", header: "Notes" },
      { key: "month", header: "Month" },
    ];
    const preamble = buildExportPreamble(targetCurrency, rates, rateDate);
    exportToCsv(
      timestampedName(`team-salaries-${selectedMonth}${convert ? `-${targetCurrency}` : ""}`),
      rows,
      columns,
      preamble ? { preamble } : undefined,
    );
    toast.success(
      convert
        ? `Exported ${rows.length} rows · all amounts in ${targetCurrency}`
        : `Exported ${rows.length} rows`
    );
  };

  const SortHeader = ({ label, sortId, className }) => (
    <TableHead
      className={cn("text-zinc-400 font-medium cursor-pointer hover:text-zinc-50 select-none", className)}
      onClick={() => handleSort(sortId)}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortKey === sortId && (sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </span>
    </TableHead>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="team-expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Team Salaries</h1>
          <p className="text-sm text-zinc-500">Monthly payroll management</p>
        </div>

        <div className="flex gap-2">
          <ExportCurrencyButton onExport={handleExport} />
          {payments.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="border-red-800 text-red-400 hover:bg-red-500/10" data-testid="delete-all-payments-btn">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Month
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-zinc-50">Delete All Payments</AlertDialogTitle>
                  <AlertDialogDescription className="text-zinc-400">
                    This will delete all {payments.length} payments for {formatMonth(selectedMonth)}. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-zinc-700 text-zinc-300">Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDeleteAllMonthPayments} 
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="confirm-delete-all-btn"
                  >
                    Delete All
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="generate-payments-btn">
                <RefreshCw className="h-4 w-4 mr-2" />
                Generate Payments
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-zinc-50">Generate Monthly Payments</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <p className="text-zinc-400 text-sm">
                  This will create payment records for all active employees ({employees.length}) for {formatMonth(selectedMonth)}.
                </p>
                <p className="text-zinc-400 text-sm">
                  Total planned: <span className="text-zinc-50 font-mono">{formatAmount(monthlySummary?.total_monthly_planned || 0, "UAH")}</span>
                </p>
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setGenerateDialogOpen(false)} className="border-zinc-700">
                    Cancel
                  </Button>
                  <Button onClick={handleGeneratePayments} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-generate-btn">
                    Generate
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Monthly Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Planned/Month</span>
              <Calendar className="h-4 w-4 text-zinc-500" />
            </div>
            <p className="text-2xl font-bold text-zinc-50">{formatAmount(monthlySummary?.total_monthly_planned || 0, "UAH")}</p>
            <p className="text-xs text-zinc-500 mt-1">{monthlySummary?.employee_count || 0} employees</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">This Month Total</span>
              <Wallet className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-orange-400">{formatAmount(monthStats.total, "UAH")}</p>
            <p className="text-xs text-zinc-500 mt-1">{formatMonth(selectedMonth)}</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Paid</span>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-400">{formatAmount(monthStats.paid, "UAH")}</p>
            <p className="text-xs text-zinc-500 mt-1">{payments.filter(p => p.status === "paid").length} payments</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Pending</span>
              <Clock className="h-4 w-4 text-sky-500" />
            </div>
            <p className="text-2xl font-bold text-sky-400">{formatAmount(monthStats.pending, "UAH")}</p>
            <p className="text-xs text-zinc-500 mt-1">{payments.filter(p => p.status === "pending").length} to pay</p>
          </CardContent>
        </Card>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost"
          onClick={() => navigateMonth(-1)}
          className="text-zinc-400 hover:text-zinc-50"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        <h2 className="text-lg font-semibold text-zinc-50 font-['Manrope']">
          {formatMonth(selectedMonth)}
        </h2>
        <Button
          variant="ghost"
          onClick={() => navigateMonth(1)}
          className="text-zinc-400 hover:text-zinc-50"
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Payments for Selected Month */}
      <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope']">
            Salary Payments - {formatMonth(selectedMonth)}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={showBonuses ? "default" : "outline"}
              onClick={() => setShowBonuses(prev => !prev)}
              className={showBonuses ? "bg-zinc-700 hover:bg-zinc-600 h-7 text-xs" : "border-zinc-700 h-7 text-xs"}
              data-testid="toggle-bonuses-btn"
            >
              {showBonuses ? "Hide Bonuses" : "Show Bonuses"}
            </Button>
            {payments.filter(p => p.status === "pending").length > 0 && (
              <Button
                size="sm"
                onClick={handleMarkAllPaid}
                className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs"
                data-testid="mark-all-paid-btn"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                Pay All
              </Button>
            )}
          </div>
        </CardHeader>

        {/* Filters Row */}
        <div className="px-6 pb-3 flex flex-wrap items-center gap-3">
          <Input
            placeholder="Search name..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            className="w-48 h-8 bg-zinc-800 border-zinc-700 text-zinc-50 text-xs"
            data-testid="filter-search-name"
          />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-32 h-8 bg-zinc-800 border-zinc-700 text-xs" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterMethod} onValueChange={setFilterMethod}>
            <SelectTrigger className="w-36 h-8 bg-zinc-800 border-zinc-700 text-xs" data-testid="filter-method">
              <SelectValue placeholder="Method" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="all">All Methods</SelectItem>
              <SelectItem value="card">Card</SelectItem>
              <SelectItem value="fop">FOP</SelectItem>
              <SelectItem value="crypto">Crypto</SelectItem>
              <SelectItem value="iban">IBAN</SelectItem>
            </SelectContent>
          </Select>
          {(searchName || filterStatus !== "all" || filterMethod !== "all") && (
            <Button size="sm" variant="ghost" onClick={() => { setSearchName(""); setFilterStatus("all"); setFilterMethod("all"); }} className="h-8 text-xs text-zinc-400">
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
          <span className="text-xs text-zinc-500 ml-auto">{sortedGrouped.length} of {groupedPayments.length} employees</span>
        </div>

        {/* Method Summary */}
        {Object.keys(methodSummary).length > 0 && (
          <div className="px-6 pb-3 flex flex-wrap gap-2">
            {Object.entries(methodSummary).sort((a, b) => b[1].pending - a[1].pending).map(([method, data]) => (
              <div key={method} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-zinc-800/70 border border-zinc-700/50 text-xs">
                <span className="text-zinc-400 capitalize">{method}:</span>
                {data.pending > 0 && <span className="text-orange-400 font-mono">{formatAmount(data.pending, "UAH")}</span>}
                {data.pending > 0 && data.paid > 0 && <span className="text-zinc-600">|</span>}
                {data.paid > 0 && <span className="text-green-400 font-mono">{formatAmount(data.paid, "UAH")}</span>}
                <span className="text-zinc-600">({data.count})</span>
              </div>
            ))}
          </div>
        )}
        <CardContent>
          {payments.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500">No payments for this month</p>
              <p className="text-xs text-zinc-600 mt-1">Click &quot;Generate Payments&quot; to create</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <SortHeader label="Employee" sortId="name" />
                  <SortHeader label="Method" sortId="method" className="text-center" />
                  <SortHeader label="Salary" sortId="salary" className="text-right" />
                  <TableHead className="text-zinc-400 font-medium text-right">Bonuses</TableHead>
                  <SortHeader label="Total" sortId="total" className="text-right" />
                  <TableHead className="text-zinc-400 font-medium text-right">Paid</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-center">Status</TableHead>
                  <TableHead className="text-zinc-400 font-medium">Date</TableHead>
                  <TableHead className="w-32"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedGrouped.map((item) => {
                  const mainPayment = item.main_payment;
                  
                  // Handle case when employee has only bonuses/partial payments but no main salary
                  // Use first bonus as reference for currency, or default to UAH
                  const firstPaymentRef = mainPayment || item.bonus_payments[0] || item.partial_payments[0];
                  if (!firstPaymentRef) return null;
                  
                  const mainCurrency = (mainPayment?.currency) || (item.bonus_payments[0]?.currency) || "UAH";
                  const salaryAmount = mainPayment?.amount || 0;
                  
                  // Convert bonuses to the main payment currency for proper summing
                  // Bonuses are stored in UAH by default
                  const bonusTotal = item.bonus_payments.reduce((sum, b) => {
                    const bonusCurr = b.currency || "UAH";
                    // Convert bonus to the same currency as main payment for display
                    if (bonusCurr === mainCurrency) {
                      return sum + b.amount;
                    }
                    // Convert: first to UAH, then to mainCurrency
                    const bonusInUAH = bonusCurr === "UAH" ? b.amount : 
                                       bonusCurr === "USD" ? b.amount * (rates?.USD || 41.5) :
                                       bonusCurr === "EUR" ? b.amount * (rates?.EUR || 43.5) : b.amount;
                    if (mainCurrency === "UAH") return sum + bonusInUAH;
                    if (mainCurrency === "USD") return sum + bonusInUAH / (rates?.USD || 41.5);
                    if (mainCurrency === "EUR") return sum + bonusInUAH / (rates?.EUR || 43.5);
                    return sum + b.amount;
                  }, 0);
                  
                  const totalAmount = salaryAmount + bonusTotal;
                  
                  // Convert total_paid_uah to mainCurrency for comparison
                  let totalPaidInMainCurrency = item.total_paid_uah;
                  if (mainCurrency === "USD") totalPaidInMainCurrency = item.total_paid_uah / (rates?.USD || 41.5);
                  else if (mainCurrency === "EUR") totalPaidInMainCurrency = item.total_paid_uah / (rates?.EUR || 43.5);
                  
                  const remaining = totalAmount - totalPaidInMainCurrency;
                  const isFullyPaid = remaining <= 0 || (mainPayment?.status === "paid" && item.bonus_payments.every(b => b.status === "paid"));
                  
                  // Use firstPaymentRef.id for key when mainPayment is null
                  const rowKey = mainPayment?.id || firstPaymentRef.id || item.employee_id;
                  
                  return (
                    <React.Fragment key={rowKey}>
                      <TableRow className="border-zinc-800 hover:bg-zinc-800/50">
                        <TableCell>
                          <p className="text-zinc-50 font-medium text-sm">{item.employee_name}</p>
                          {(item.partial_payments.length > 0 || item.bonus_payments.length > 0) && (
                            <p className="text-xs text-zinc-500 mt-0.5">
                              {item.partial_payments.length > 0 && `+${item.partial_payments.length} partial`}
                              {item.partial_payments.length > 0 && item.bonus_payments.length > 0 && ", "}
                              {item.bonus_payments.length > 0 && (
                                <span className="text-purple-400">+{item.bonus_payments.length} bonus</span>
                              )}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {getPaymentMethodDisplay(item.payment_method, item.payment_method_bank, item.crypto_network)}
                            {(() => {
                              const emp = employees.find(e => e.id === item.employee_id);
                              if (emp) {
                                return (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-5 w-5 p-0 text-green-500 hover:text-green-400 hover:bg-green-500/10"
                                    onClick={() => setPaymentDetailsModal({ open: true, employee: emp, payment: item })}
                                    title="Реквізити"
                                  >
                                    <Banknote className="h-3.5 w-3.5" />
                                  </Button>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-zinc-50">{formatAmount(salaryAmount, mainCurrency)}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={cn("font-mono", bonusTotal > 0 ? "text-purple-400" : "text-zinc-600")}>
                            {bonusTotal > 0 ? formatAmount(bonusTotal, mainCurrency) : "—"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-zinc-50 font-bold">{formatAmount(totalAmount, mainCurrency)}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={cn("font-mono", totalPaidInMainCurrency > 0 ? "text-green-400" : "text-zinc-600")}>
                            {formatAmount(totalPaidInMainCurrency, mainCurrency)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          {isFullyPaid ? (
                            <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Paid
                            </Badge>
                          ) : totalPaidInMainCurrency > 0 ? (
                            <Badge variant="outline" className="bg-sky-500/10 text-sky-400 border-sky-500/20">
                              <SplitSquareHorizontal className="h-3 w-3 mr-1" />
                              Partial
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {mainPayment?.paid_date ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs text-zinc-400 font-mono">
                                {new Date(mainPayment.paid_date).toLocaleDateString()}
                              </span>
                              {mainPayment?.paid_by_name && (
                                <span className="text-[10px] text-zinc-500">{mainPayment.paid_by_name}</span>
                              )}
                              {mainPayment?.paid_source && (
                                <span className="text-[10px] text-blue-400/70">{mainPayment.paid_source}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-zinc-600">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setBonusDialog({ open: true, employee: item })}
                              className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 h-8 px-2"
                              title="Add bonus"
                              data-testid={`bonus-${rowKey}`}
                            >
                              <Gift className="h-4 w-4" />
                            </Button>
                            {!isFullyPaid && mainPayment && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setPartialPaymentDialog({ open: true, payment: mainPayment })}
                                  className="text-sky-400 hover:text-sky-300 hover:bg-sky-500/10 h-8 px-2"
                                  title="Partial payment"
                                  data-testid={`partial-${mainPayment.id}`}
                                >
                                  <SplitSquareHorizontal className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleMarkPaid(mainPayment.id)}
                                  className="text-green-400 hover:text-green-300 hover:bg-green-500/10 h-8 px-2"
                                  data-testid={`pay-${mainPayment.id}`}
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-1" />
                                  Full
                                </Button>
                              </>
                            )}
                            {mainPayment && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeletePayment(mainPayment.id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 px-2"
                                data-testid={`delete-${mainPayment.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      
                      {/* Bonus rows */}
                      {showBonuses && item.bonus_payments.map((bonus, idx) => (
                        <TableRow key={bonus.id} className="border-zinc-800/50 bg-purple-950/10 hover:bg-purple-950/20">
                          <TableCell className="pl-8">
                            <div className="flex flex-col gap-0.5">
                              <div className="flex items-center gap-2">
                                <Gift className="h-3 w-3 text-purple-400" />
                                <span className="text-sm text-purple-300">{bonus.task_name || bonus.notes || `Bonus ${idx + 1}`}</span>
                              </div>
                              {bonus.type === "art_director_review" && (
                                <div className="flex items-center gap-2 ml-5 text-[10px] text-zinc-500">
                                  <span>Ціна артиста:</span>
                                  <span className="text-zinc-400 font-mono">₴{bonus.artist_price_uah?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '—'}</span>
                                  <span className="text-purple-400">× {bonus.bonus_percent || 0}%</span>
                                </div>
                              )}
                              {bonus.bonus_type === "pm_profit" && bonus.bonus_revenue && (() => {
                                // Use bonus_currency if available, otherwise parse from notes (fallback for old records)
                                const currency = bonus.bonus_currency || getCurrencyFromNotes(bonus.notes) || 'USD';
                                const symbol = currency === 'UAH' ? '₴' : currency === 'EUR' ? '€' : '$';
                                return (
                                <div className="flex items-center gap-2 ml-5 text-[10px] text-zinc-500">
                                  <span>Revenue:</span>
                                  <span className="text-green-400 font-mono">{symbol}{bonus.bonus_revenue?.toLocaleString()}</span>
                                  <span>Costs:</span>
                                  <span className="text-red-400 font-mono">{symbol}{bonus.bonus_costs?.toLocaleString()}</span>
                                  <span>Profit:</span>
                                  <span className="text-blue-400 font-mono">{symbol}{bonus.bonus_profit?.toLocaleString()}</span>
                                </div>
                                );
                              })()}
                            </div>
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-right">
                            <span className="font-mono text-purple-400">{formatAmount(bonus.amount, bonus.currency || "UAH")}</span>
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-right">
                            <span className={cn("font-mono text-sm", bonus.status === "paid" ? "text-green-400" : "text-zinc-600")}>
                              {bonus.status === "paid" ? formatAmount(bonus.amount, bonus.currency || "UAH") : "—"}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            {bonus.status === "paid" ? (
                              <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20 text-xs">
                                Paid
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/20 text-xs">
                                Pending
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {bonus.paid_date && (
                              <span className="text-xs text-zinc-400 font-mono">
                                {new Date(bonus.paid_date).toLocaleDateString()}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {bonus.status !== "paid" && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleMarkPaid(bonus.id, bonus.type === 'art_director_review')}
                                  className="text-green-400 hover:text-green-300 hover:bg-green-500/10 h-7 px-2"
                                >
                                  <CheckCircle2 className="h-3 w-3" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeletePayment(bonus.id, bonus.type === 'art_director_review')}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Bonus Dialog */}
      <Dialog open={bonusDialog.open} onOpenChange={(open) => setBonusDialog({ open, employee: bonusDialog.employee })}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Gift className="h-5 w-5 text-purple-400" />
              Add Bonus
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <p className="text-zinc-400 text-sm mb-2">
                Employee: <span className="text-zinc-50">{bonusDialog.employee?.employee_name}</span>
              </p>
              <p className="text-zinc-400 text-sm">
                Month: <span className="text-zinc-50">{formatMonth(selectedMonth)}</span>
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Bonus Type</Label>
              <Select value={bonusType} onValueChange={setBonusType}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="project_bonus" className="text-zinc-50">Project Bonus</SelectItem>
                  <SelectItem value="performance" className="text-zinc-50">Performance Bonus</SelectItem>
                  <SelectItem value="overtime" className="text-zinc-50">Overtime</SelectItem>
                  <SelectItem value="referral" className="text-zinc-50">Referral Bonus</SelectItem>
                  <SelectItem value="holiday" className="text-zinc-50">Holiday Bonus</SelectItem>
                  <SelectItem value="other" className="text-zinc-50">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-2">
                <Label className="text-zinc-300">Amount</Label>
                <Input
                  type="number"
                  value={bonusAmount}
                  onChange={(e) => setBonusAmount(e.target.value)}
                  placeholder="Enter amount..."
                  className="bg-zinc-950 border-zinc-800 text-zinc-50"
                  data-testid="bonus-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Currency</Label>
                <Select value={bonusCurrency} onValueChange={setBonusCurrency}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="UAH" className="text-zinc-50">₴ UAH</SelectItem>
                    <SelectItem value="USD" className="text-zinc-50">$ USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setBonusDialog({ open: false, employee: null })} className="border-zinc-700">
                Cancel
              </Button>
              <Button onClick={handleAddBonus} className="bg-purple-500 hover:bg-purple-600" data-testid="confirm-bonus-btn">
                Add Bonus
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Partial Payment Dialog */}
      <Dialog open={partialPaymentDialog.open} onOpenChange={(open) => setPartialPaymentDialog({ open, payment: partialPaymentDialog.payment })}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Partial Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <p className="text-zinc-400 text-sm mb-2">
                Employee: <span className="text-zinc-50">{partialPaymentDialog.payment?.employee_name}</span>
              </p>
              <p className="text-zinc-400 text-sm">
                Full amount: <span className="text-zinc-50 font-mono">${partialPaymentDialog.payment?.amount?.toLocaleString()}</span>
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-2">
                <Label className="text-zinc-300">Payment Amount</Label>
                <Input
                  type="number"
                  value={partialAmount}
                  onChange={(e) => setPartialAmount(e.target.value)}
                  placeholder="Enter amount..."
                  className="bg-zinc-950 border-zinc-800 text-zinc-50"
                  data-testid="partial-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Currency</Label>
                <Select value={partialCurrency} onValueChange={setPartialCurrency}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="UAH" className="text-zinc-50">₴ UAH</SelectItem>
                    <SelectItem value="USD" className="text-zinc-50">$ USD</SelectItem>
                    <SelectItem value="EUR" className="text-zinc-50">€ EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setPartialPaymentDialog({ open: false, payment: null })} className="border-zinc-700">
                Cancel
              </Button>
              <Button onClick={handlePartialPayment} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-partial-btn">
                Pay
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Monthly Summary Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-orange-500" />
            Monthly Expense History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!monthlySummary?.monthly_breakdown?.length ? (
            <p className="text-zinc-500 text-center py-6">No expense history yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400 font-medium">Month</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Planned</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Bonuses</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Pending</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Paid</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Total</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlySummary.monthly_breakdown.map((row) => {
                  const isPaid = row.pending === 0 && row.paid > 0;
                  const isPartial = row.pending > 0 && row.paid > 0;
                  const isPending = row.pending > 0 && row.paid === 0;
                  
                  return (
                    <TableRow 
                      key={row.month} 
                      className={cn(
                        "border-zinc-800 hover:bg-zinc-800/50 cursor-pointer",
                        row.month === selectedMonth && "bg-zinc-800/30"
                      )}
                      onClick={() => setSelectedMonth(row.month)}
                    >
                      <TableCell>
                        <span className="text-zinc-50 font-medium text-sm">{formatMonth(row.month)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono text-zinc-400">{formatAmount(row.planned, "UAH")}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={cn("font-mono", row.bonuses > 0 ? "text-purple-400" : "text-zinc-600")}>
                          {formatAmount(row.bonuses || 0, "UAH")}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={cn("font-mono", row.pending > 0 ? "text-orange-400" : "text-zinc-600")}>
                          {formatAmount(row.pending, "UAH")}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={cn("font-mono", row.paid > 0 ? "text-green-400" : "text-zinc-600")}>
                          {formatAmount(row.paid, "UAH")}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono text-zinc-50 font-medium">{formatAmount(row.total, "UAH")}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        {isPaid && (
                          <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20">
                            Complete
                          </Badge>
                        )}
                        {isPartial && (
                          <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20">
                            Partial
                          </Badge>
                        )}
                        {isPending && row.total > 0 && (
                          <Badge variant="outline" className="bg-sky-500/10 text-sky-400 border-sky-500/20">
                            Pending
                          </Badge>
                        )}
                        {row.total === 0 && (
                          <Badge variant="outline" className="bg-zinc-800 text-zinc-500 border-zinc-700">
                            No Data
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Payment Details Modal */}
      <Dialog open={paymentDetailsModal.open} onOpenChange={(open) => !open && setPaymentDetailsModal({ open: false, employee: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Banknote className="h-5 w-5 text-green-400" />
              Реквізити для оплати
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Employee Info */}
            {paymentDetailsModal.employee && (
              <div className="p-4 bg-zinc-800/50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Співробітник:</span>
                  <span className="text-zinc-50 font-medium">{paymentDetailsModal.employee.name}</span>
                </div>
                {paymentDetailsModal.payment && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Зарплата:</span>
                      <span className="text-orange-400 font-mono font-bold">
                        {formatAmount(paymentDetailsModal.payment.salary_amount || paymentDetailsModal.employee.salary, paymentDetailsModal.employee.currency || "UAH")}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Місяць:</span>
                      <span className="text-zinc-50">{formatMonth(paymentDetailsModal.payment.month || selectedMonth)}</span>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Payment Methods */}
            {paymentDetailsModal.employee?.payment_methods ? (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-zinc-400">Реквізити:</h4>
                
                {/* Preferred method badge */}
                {paymentDetailsModal.employee.payment_methods.preferred_method && (
                  <Badge className="bg-green-900/30 text-green-400 border-green-700 mb-2">
                    Preferred: {paymentDetailsModal.employee.payment_methods.preferred_method === "card" ? "Карта" : paymentDetailsModal.employee.payment_methods.preferred_method === "fop" ? "ФОП" : "Крипто"}
                  </Badge>
                )}

                {/* Card */}
                {paymentDetailsModal.employee.payment_methods.card && Object.values(paymentDetailsModal.employee.payment_methods.card).some(v => v) && (
                  <div className="p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                    <div className="flex items-center gap-2 mb-2">
                      <CreditCard className="h-4 w-4 text-green-400" />
                      <span className="text-green-400 font-medium">Карта</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      {paymentDetailsModal.employee.payment_methods.card.card_number && (
                        <div className="flex justify-between items-center">
                          <span className="text-zinc-400">Номер карти:</span>
                          <div className="flex items-center gap-2">
                            <span className="text-zinc-50 font-mono">{paymentDetailsModal.employee.payment_methods.card.card_number}</span>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { navigator.clipboard.writeText(paymentDetailsModal.employee.payment_methods.card.card_number); toast.success("Скопійовано"); }}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                      {paymentDetailsModal.employee.payment_methods.card.bank && (
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Банк:</span>
                          <span className="text-zinc-50">{paymentDetailsModal.employee.payment_methods.card.bank}</span>
                        </div>
                      )}
                      {paymentDetailsModal.employee.payment_methods.card.recipient_name && (
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Отримувач:</span>
                          <span className="text-zinc-50">{paymentDetailsModal.employee.payment_methods.card.recipient_name}</span>
                        </div>
                      )}
                      {paymentDetailsModal.employee.payment_methods.card.recipient_iban && (
                        <div className="flex justify-between items-center">
                          <span className="text-zinc-400">IBAN:</span>
                          <div className="flex items-center gap-2">
                            <span className="text-zinc-50 font-mono text-xs">{paymentDetailsModal.employee.payment_methods.card.recipient_iban}</span>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { navigator.clipboard.writeText(paymentDetailsModal.employee.payment_methods.card.recipient_iban); toast.success("Скопійовано"); }}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                      {paymentDetailsModal.employee.payment_methods.card.recipient_ipn && (
                        <div className="flex justify-between">
                          <span className="text-zinc-400">ІПН:</span>
                          <span className="text-zinc-50 font-mono">{paymentDetailsModal.employee.payment_methods.card.recipient_ipn}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* FOP */}
                {paymentDetailsModal.employee.payment_methods.fop && Object.values(paymentDetailsModal.employee.payment_methods.fop).some(v => v) && (
                  <div className="p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="h-4 w-4 text-purple-400" />
                      <span className="text-purple-400 font-medium">ФОП</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      {paymentDetailsModal.employee.payment_methods.fop.company_name && (
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Назва:</span>
                          <span className="text-zinc-50">{paymentDetailsModal.employee.payment_methods.fop.company_name}</span>
                        </div>
                      )}
                      {paymentDetailsModal.employee.payment_methods.fop.iban && (
                        <div className="flex justify-between items-center">
                          <span className="text-zinc-400">IBAN:</span>
                          <div className="flex items-center gap-2">
                            <span className="text-zinc-50 font-mono text-xs">{paymentDetailsModal.employee.payment_methods.fop.iban}</span>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { navigator.clipboard.writeText(paymentDetailsModal.employee.payment_methods.fop.iban); toast.success("Скопійовано"); }}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                      {paymentDetailsModal.employee.payment_methods.fop.edrpou && (
                        <div className="flex justify-between">
                          <span className="text-zinc-400">ЄДРПОУ:</span>
                          <span className="text-zinc-50 font-mono">{paymentDetailsModal.employee.payment_methods.fop.edrpou}</span>
                        </div>
                      )}
                      {paymentDetailsModal.employee.payment_methods.fop.bank_name && (
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Банк:</span>
                          <span className="text-zinc-50">{paymentDetailsModal.employee.payment_methods.fop.bank_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Crypto */}
                {paymentDetailsModal.employee.payment_methods.crypto && Object.values(paymentDetailsModal.employee.payment_methods.crypto).some(v => v) && (
                  <div className="p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                    <div className="flex items-center gap-2 mb-2">
                      <Bitcoin className="h-4 w-4 text-orange-400" />
                      <span className="text-orange-400 font-medium">Крипто</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      {paymentDetailsModal.employee.payment_methods.crypto.network && (
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Мережа:</span>
                          <span className="text-zinc-50">{paymentDetailsModal.employee.payment_methods.crypto.network}</span>
                        </div>
                      )}
                      {paymentDetailsModal.employee.payment_methods.crypto.wallet_address && (
                        <div className="flex justify-between items-center">
                          <span className="text-zinc-400">Гаманець:</span>
                          <div className="flex items-center gap-2">
                            <span className="text-zinc-50 font-mono text-xs truncate max-w-[200px]">{paymentDetailsModal.employee.payment_methods.crypto.wallet_address}</span>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { navigator.clipboard.writeText(paymentDetailsModal.employee.payment_methods.crypto.wallet_address); toast.success("Скопійовано"); }}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <Wallet className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>Реквізити не заповнені</p>
                <p className="text-xs mt-1">Співробітник ще не вказав свої платіжні дані</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Paid Details Dialog */}
      <Dialog open={paidDetailsDialog.open} onOpenChange={(v) => !v && setPaidDetailsDialog({ open: false, paymentId: null, isStaffBonus: false })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Payment Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Paid By</label>
              <Select value={paidBy} onValueChange={setPaidBy}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 h-9" data-testid="paid-by-select">
                  <SelectValue placeholder="Select person..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {adminStaff.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <PaymentSourceSelector value={paidSource} onChange={setPaidSource} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" className="border-zinc-700" onClick={() => setPaidDetailsDialog({ open: false, paymentId: null, isStaffBonus: false })}>
                Cancel
              </Button>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={handleConfirmPaid} data-testid="confirm-paid-btn">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                Mark Paid
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamExpensesPage;
